// Error screen at the root of both routers, replacing react-router's raw
// "Unexpected Application Error!". If a deploy happened since the tab was
// opened, the error almost surely comes from the old front: reload, once per
// URL so it can never loop.
import { useEffect, useState } from "react";
import { useRouteError } from "react-router";
import { Button } from "@shared/components/ui/button";
import { checkForNewBuild } from "./build-version";
import { useT } from "./locale";

const RELOAD_KEY = "hune-reloaded-after-error";

function reloadedOnce() {
  try {
    return window.sessionStorage.getItem(RELOAD_KEY) === window.location.href;
  } catch {
    return true;
  }
}

function markReloaded() {
  try {
    window.sessionStorage.setItem(RELOAD_KEY, window.location.href);
    return true;
  } catch {
    return false;
  }
}

/**
 * Logs the error, then reloads once if a newer build is deployed. True while
 * the check runs (render nothing: the page is about to reload). `enabled`
 * false skips it (a 404 is not a crash).
 */
export function useReloadIfStale(error: unknown, enabled = true) {
  const [checking, setChecking] = useState(enabled);

  useEffect(() => {
    if (!enabled) return;
    console.error(error);
    let cancelled = false;
    void checkForNewBuild({ force: true }).then((stale) => {
      if (cancelled) return;
      if (stale && !reloadedOnce() && markReloaded()) window.location.reload();
      else setChecking(false);
    });
    return () => {
      cancelled = true;
    };
  }, [error, enabled]);

  return checking;
}

export function RouteError() {
  const error = useRouteError();
  const checking = useReloadIfStale(error);
  const copy = useT().common.states;

  if (checking) return null;
  return (
    <main className="grid min-h-screen place-items-center bg-background px-4">
      <div className="grid max-w-[420px] gap-4 text-center">
        <h1 className="m-0 font-serif text-[32px] leading-tight font-normal tracking-tight">{copy.crash.title}</h1>
        <p className="m-0 text-[13.5px] text-muted-foreground">
          {copy.crash.body}
        </p>
        <Button className="justify-self-center" onClick={() => window.location.reload()}>
          {copy.reload}
        </Button>
      </div>
    </main>
  );
}
